import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import originalFilter from './originalFilter.js';

const DataContext = createContext(null);

// const APIBASE = "http://127.0.0.1:8000"
const APIBASE = "https://api.eyesonenforcement.org"

// Internal variable used to simulate the public/internal domains
// IMPORTANT: Set to Null for deployment
// "public"   (www.eyesonenforcement.org)
// "internal" (app.eyesonenforcement.org) 
// "test"     (test.eyesonenforcement.org) 
const DEPLOYMENT_MODE = null

async function GetApi(path) {
    let headers = {}
    if (DEPLOYMENT_MODE) {
        headers["Deployment-Mode"] = DEPLOYMENT_MODE
    }

    const response = await fetch (APIBASE + path, {
        headers: headers
    })

    const jsonResponse = await response.json()
    return jsonResponse
}

async function PostApi(path, body) {
    let headers = {"Content-Type": "application/json"}
    if (DEPLOYMENT_MODE) {
        headers["Deployment-Mode"] = DEPLOYMENT_MODE
    }

    const response = await fetch(APIBASE + path, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body)
    }) 

    const jsonResponse = await response.json()
    return jsonResponse
}

export function DataProvider({ children }) {
    const [docs, setDocs] = useState(null);
    const [filter, setFilter] = useState(originalFilter);
    const [sort, setSort] = useState("");
    const [isLoading, setLoading] = useState(true);
    const [stats, setStats] = useState({ count: null, epaRegions: [], documents: [], minYear: "", maxYear: "", statutes: [], statuteCount: {} });
    const [pageNumber, setPageNumber] = useState(1);
    const [pageLimit, setPageLimit] = useState(25);
    const [apiUrl, setApiUrl] = useState(APIBASE);

    const buildRequestBody = () => {
        return {
            "filter": filter,
            "sort": sort,
            "pageNumber": pageNumber,
            "pageLimit": pageLimit,
        }
    }

    useEffect(() => {
        async function fetchData() {
            let body = buildRequestBody()
            let res = await PostApi("/documents", body)
            let statRes = await GetApi("/stats")
            setDocs(res.documents);
            setStats(statRes);
            setLoading(false);
        }
        fetchData();
    }, []);
    
    async function fetchDocuments(filter, newPageNumber=null, newPageLimit=null, newSort=null) {
        let body = buildRequestBody()
        if (filter != null)
            body["filter"] = filter
        if (newPageNumber != null)
            body["pageNumber"] = newPageNumber
        if (newPageLimit != null)
            body["pageLimit"] = newPageLimit
        if (newSort != null)
            body["sort"] = newSort
        let res = await PostApi("/documents", body)
        setDocs(res.documents);
        setStats(prev => ({ ...prev, count: res.count, statuteCount: res.statute_count }))
    }

    const applyFilter = useCallback(async (filterFn, newFilter) => {
        fetchDocuments(newFilter)
        setFilter(newFilter);
    }, []);

    const resetFilter = useCallback(() => {
        fetchDocuments(originalFilter)
        setFilter(originalFilter);
    }, []);

    const applySort = useCallback((newSort) => {
        fetchDocuments(null, null, null, newSort)
        setSort(newSort)
    });

    const applyPageNumber = useCallback((newPageNumber) => {
        window.scrollTo({ top: document.getElementById("document-section-header").offsetTop - 80 })
        setPageNumber(newPageNumber)
        fetchDocuments(null, newPageNumber)
    })

    const applyPageLimit = useCallback((newPageLimit) => {
        window.scrollTo({ top: document.getElementById("document-section-header").offsetTop - 80 })
        setPageLimit(newPageLimit)
        fetchDocuments(null, null, newPageLimit)
    })

    const getDoc = useCallback((hashId) => {
        return GetApi('/document/' + hashId)
    }, [])

    return (
        <DataContext.Provider value={{ docs, apiUrl, isLoading, stats, filter, sort, applySort, pageNumber, pageLimit, getDoc, applyFilter, resetFilter, applyPageNumber, applyPageLimit }}>
            {children}
        </DataContext.Provider>
    );
}

export function useDocs() {
    return useContext(DataContext);
}